import { createClient } from "@/auth/client";
import { InstagramAccount, SocialProfile, InstagramConnectionState } from "../types";
import { BASE_URL } from "@/config";
import { ERROR_MESSAGES } from "@/lib/dify/errorUtils";

export class InstagramService {
  private static supabase = createClient();

  static async fetchInstagramAccounts(): Promise<{ accounts: InstagramAccount[]; error: string | null }> {
    try {
      // Add timeout to prevent infinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch('/api/social/accounts', {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(ERROR_MESSAGES.SOCIAL.FETCH_ACCOUNTS_FAILED);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Transform the unified response to match the expected format
        const typedAccounts: InstagramAccount[] = (data.accounts || []).map((account: any) => ({
          id: account.id,
          username: account.username,
          name: account.name,
          profile_picture_url: account.profile_picture_url,
          fbPageId: account.connectionType === 'instagram_direct' ? "direct_connection" : account.facebookConnectionId,
          fbPageName: account.connectionType === 'instagram_direct' ? "Instagram Direct Connection" : "Instagram via Facebook",
          connectionId: account.connectionId,
          connectionType: account.connectionType,
          isConnectionOnly: account.isConnectionOnly || false
        }));
        
        return { accounts: typedAccounts, error: null };
      } else {
        return { accounts: [], error: data.error || 'Unknown error' };
      }
    } catch (err: any) {
      console.error("Error loading Instagram accounts:", err);
      if (err.name === 'AbortError') {
        return { accounts: [], error: ERROR_MESSAGES.GENERAL.TIMEOUT };
      }
      return { accounts: [], error: err.message || ERROR_MESSAGES.SOCIAL.LOAD_INSTAGRAM_FAILED };
    }
  }

  /**
   * Poll instagram_direct.social_fetch for a given connection/account until it reports a success-like status.
   * Success statuses: 'success', 'succeeded', 'completed', 'done'
   * Failure statuses: 'failed', 'error', 'canceled', 'cancelled'
   */
  static async waitForSocialFetchSuccess(
    identifiers: { connectionId: string; connectionType?: 'instagram_direct' | 'instagram_via_facebook' },
    options?: { intervalMs?: number; timeoutMs?: number; requireNewSinceMs?: number }
  ): Promise<{ status: string }> {
    const intervalMs = options?.intervalMs ?? 10000;
    const timeoutMs = options?.timeoutMs ?? 10 * 60 * 1000; // 10 minutes default
    const requireNewSinceMs = options?.requireNewSinceMs;

    if (!identifiers.connectionId) {
      throw new Error(ERROR_MESSAGES.SOCIAL.SELECT_ACCOUNT_FAILED);
    }

    console.log('[IG POLL] waitForSocialFetchSuccess:init', { connectionId: identifiers.connectionId, intervalMs, timeoutMs });

    const start = Date.now();
    // Server schema allows: error, partial_success, success, in_progress
    const successSet = new Set(['success', 'partial_success', 'succeeded', 'completed', 'done']);
    const failureSet = new Set(['failed', 'error', 'canceled', 'cancelled']);
    let sawNonSuccessSinceStart = false;

    while (true) {
      if (Date.now() - start > timeoutMs) {
        console.warn('[IG POLL] waitForSocialFetchSuccess:timeout');
        throw new Error(ERROR_MESSAGES.SOCIAL.SOCIAL_FETCH_TIMEOUT);
      }

      try {
        console.log('[IG POLL] waitForSocialFetchSuccess:query', { connectionId: identifiers.connectionId, at: new Date().toISOString() });
        const qs = new URLSearchParams({ connectionId: identifiers.connectionId });
        if (identifiers.connectionType) {
          qs.set('connectionType', identifiers.connectionType);
        }
        const res = await fetch(`/api/instagram/social-fetch-status?${qs.toString()}` , {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          // Prevent Next.js from caching during polling
          cache: 'no-store'
        } as RequestInit);
        const json = await res.json();
        if (!res.ok) {
          console.warn('waitForSocialFetchSuccess: API error while polling:', json?.error || res.statusText);
        }

        const status = (json?.status || '').toString().toLowerCase();
        console.log('[IG POLL] waitForSocialFetchSuccess:result', { connectionId: identifiers.connectionId, status, at: new Date().toISOString() });
        if (status) {
          if (successSet.has(status)) {
            // If the caller requires a new result after a point in time, try to validate recency.
            if (requireNewSinceMs) {
              // Try common timestamp fields from the API payload
              const tsCandidate =
                json?.updatedAt || json?.updated_at || json?.lastUpdatedAt || json?.last_updated_at ||
                json?.completedAt || json?.completed_at || json?.timestamp || json?.ts || null;
              let tsMs: number | null = null;
              if (typeof tsCandidate === 'number') tsMs = tsCandidate;
              else if (typeof tsCandidate === 'string') {
                const parsed = Date.parse(tsCandidate);
                tsMs = isNaN(parsed) ? null : parsed;
              }

              if (tsMs !== null) {
                if (tsMs > requireNewSinceMs) {
                  console.log('[IG POLL] waitForSocialFetchSuccess:success(newer-than-start)', { status, tsMs, requireNewSinceMs });
                  return { status };
                }
                // Stale success (before trigger). Keep polling.
                console.log('[IG POLL] waitForSocialFetchSuccess:ignore-stale-success', { status, tsMs, requireNewSinceMs });
              } else {
                // No timestamp available. As a heuristic, only accept success if we've seen a non-success after start
                // or enough time has elapsed to reasonably assume a new cycle.
                const elapsed = Date.now() - start;
                if (sawNonSuccessSinceStart || elapsed > intervalMs * 2) {
                  console.log('[IG POLL] waitForSocialFetchSuccess:success(heuristic-no-ts)', { status, elapsed, sawNonSuccessSinceStart });
                  return { status };
                }
                console.log('[IG POLL] waitForSocialFetchSuccess:ignore-initial-success-no-ts', { status });
              }
            } else {
              console.log('[IG POLL] waitForSocialFetchSuccess:success', { status });
              return { status };
            }
          }
          if (failureSet.has(status)) {
            console.error('[IG POLL] waitForSocialFetchSuccess:failure', { status });
            throw new Error(`${ERROR_MESSAGES.SOCIAL.INDEXING_FAILED} with status: ${status}`);
          }
          // Mark that we observed a non-success after start
          if (!successSet.has(status)) {
            sawNonSuccessSinceStart = true;
          }
        } else {
          // No status => treat as non-success observation
          sawNonSuccessSinceStart = true;
        }
      } catch (e) {
        // Swallow transient errors and continue until timeout
        console.debug('[IG POLL] waitForSocialFetchSuccess:transient', e);
      }

      await new Promise(res => setTimeout(res, intervalMs));
    }
  }

  static async fetchSocialConnections(userId: string): Promise<{ connections: any[]; error: string | null }> {
    try {
      const { data: connections, error: connectionsError } = await this.supabase
        .from('connected_social_accounts')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active');

      if (connectionsError) {
        console.error("Error fetching connected social accounts:", connectionsError);
        return { connections: [], error: connectionsError.message };
      }

      return { connections: connections || [], error: null };
    } catch (err: any) {
      console.error("Error fetching social connections:", err);
      return { connections: [], error: err.message };
    }
  }

  static async getConnectionState(authUser: any): Promise<InstagramConnectionState> {
    // Check for direct Instagram connection and Instagram via Facebook connection
    const { connections } = await this.fetchSocialConnections(authUser.id);
    const directConnection = connections?.find(conn => conn.connection_type === 'instagram_direct');
    const facebookInstagramConnection = connections?.find(conn => conn.connection_type === 'instagram_via_facebook');
    
    const hasDirectConnection = !!directConnection;
    const hasInstagramViaFacebook = !!facebookInstagramConnection;
    
    let directProfile: SocialProfile | null = null;
    let facebookProfile: SocialProfile | null = null;

    if (hasDirectConnection) {
      directProfile = {
        username: directConnection.username || "Instagram User",
        picture_url: directConnection.profile_picture_url,
        connected_at: directConnection.connected_at || new Date().toISOString()
      };
    }

    if (hasInstagramViaFacebook) {
      // Always serve Facebook profile pictures via our proxy to avoid expiring CDN signatures
      const fbProxyUrl = `${BASE_URL}/api/proxy/facebook-picture?facebook_id=${encodeURIComponent(
        facebookInstagramConnection.account_id
      )}&type=large&v=${encodeURIComponent(facebookInstagramConnection.id)}`;

      facebookProfile = {
        name: facebookInstagramConnection.display_name || "Facebook User",
        picture_url: fbProxyUrl,
        connected_at: facebookInstagramConnection.connected_at || new Date().toISOString(),
        connectionId: facebookInstagramConnection.id
      };
    }

    // Determine current connection type (prioritize direct if both exist)
    let currentConnectionType: 'instagram_direct' | 'instagram_via_facebook' | null = null;
    if (hasDirectConnection) {
      currentConnectionType = 'instagram_direct';
    } else if (hasInstagramViaFacebook) {
      currentConnectionType = 'instagram_via_facebook';
    }

    // Fetch accounts based on connection type
    let accounts: InstagramAccount[] = [];

    // For direct Instagram connections, use the existing logic
    if (hasDirectConnection) {
      try {
        const { accounts: directAccounts, error } = await this.fetchInstagramAccounts();
        if (error) {
          console.error("Error fetching Instagram accounts:", error);
          // Continue with empty accounts array - connection exists but accounts failed to load
        } else {
          accounts = directAccounts.filter(acc => acc.connectionType === 'instagram_direct');
        }
      } catch (error) {
        console.error("Exception fetching Instagram accounts:", error);
        // Continue with empty accounts array - connection exists but accounts failed to load
      }
    }

    // For Facebook Instagram connections, check for selected accounts in ig_via_fb_connected_ig_accounts
    if (hasInstagramViaFacebook) {
      try {
        const { data: selectedAccounts, error: selectedError } = await this.supabase
          .from('ig_via_fb_connected_ig_accounts')
          .select('*')
          .eq('user_id', authUser.id)
          .eq('connected_social_account_id', facebookInstagramConnection.id)
          .eq('status', 'active');

        if (selectedError) {
          console.error("Error fetching selected Instagram accounts:", selectedError);
        } else if (selectedAccounts && selectedAccounts.length > 0) {
          // Convert selected accounts to the expected format
          const facebookInstagramAccounts: InstagramAccount[] = selectedAccounts.map(account => ({
            id: account.account_id,
            username: account.username,
            name: account.display_name,
            profile_picture_url: account.profile_picture_url,
            fbPageId: "selected_instagram_account",
            fbPageName: account.display_name,
            connectionId: facebookInstagramConnection.id,
            connectionType: 'instagram_via_facebook'
          }));
          accounts.push(...facebookInstagramAccounts);
        } else {
          // No selected accounts found - Facebook is connected but no Instagram accounts selected
          // In this case, we should not show the connection as having accounts
          // The UI should prompt for Instagram account selection
          console.log("Facebook connected but no Instagram accounts selected");
        }
      } catch (error) {
        console.error("Error fetching selected Instagram accounts:", error);
      }
    }

    return {
      hasDirectConnection,
      hasInstagramViaFacebook,
      currentConnectionType,
      directProfile,
      facebookProfile,
      accounts
    };
  }

  static async disconnectInstagram(connectionId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      const response = await fetch('/api/instagram/disconnect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ connectionId }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || ERROR_MESSAGES.SOCIAL.DISCONNECT_FAILED);
      }

      return { success: true, error: null };
    } catch (err: any) {
      console.error("Error during Instagram disconnect:", err);
      return { success: false, error: err.message || ERROR_MESSAGES.SOCIAL.DISCONNECT_FAILED };
    }
  }

  static async disconnectAllInstagramConnections(userId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      const { data: connections, error: fetchError } = await this.supabase
        .from('connected_social_accounts')
        .select('id')
        .eq('user_id', userId)
        .like('connection_type', '%instagram%');

      if (fetchError) {
        throw new Error(ERROR_MESSAGES.SOCIAL.FETCH_ACCOUNTS_FAILED);
      }

      if (connections && connections.length > 0) {
        for (const connection of connections) {
          const { success, error } = await this.disconnectInstagram(connection.id);
          if (!success) {
            throw new Error(error || ERROR_MESSAGES.SOCIAL.DISCONNECT_FAILED);
          }
        }
      }

      return { success: true, error: null };
    } catch (err: any) {
      console.error("Error disconnecting all Instagram connections:", err);
      return { success: false, error: err.message };
    }
  }

  static async disconnectByConnectionType(userId: string, connectionType: 'instagram_direct' | 'instagram_via_facebook'): Promise<{ success: boolean; error: string | null }> {
    try {
      const { data: connections, error: fetchError } = await this.supabase
        .from('connected_social_accounts')
        .select('id')
        .eq('user_id', userId)
        .eq('connection_type', connectionType);

      if (fetchError) {
        throw new Error(ERROR_MESSAGES.SOCIAL.FETCH_ACCOUNTS_FAILED);
      }

      if (connections && connections.length > 0) {
        for (const connection of connections) {
          const { success, error } = await this.disconnectInstagram(connection.id);
          if (!success) {
            throw new Error(error || ERROR_MESSAGES.SOCIAL.DISCONNECT_FAILED);
          }
        }
      }

      return { success: true, error: null };
    } catch (err: any) {
      console.error("Error disconnecting Instagram connections by type:", err);
      return { success: false, error: err.message };
    }
  }

  static async triggerInstagramIndex(accountId: string, connectionId: string): Promise<{ success: boolean; error: string | null; operationId?: string; statusUrl?: string }> {
    try {
      const response = await fetch('/api/instagram/index-noparent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ connectionId, accountId }),
      });

      let result: any = null;
      try {
        result = await response.json();
      } catch {
        // Non-JSON response body; leave result as null
      }

      if (!response.ok) {
        const serverErr = result?.error;
        const msg = typeof serverErr === 'string'
          ? serverErr
          : serverErr
            ? JSON.stringify(serverErr)
            : (response.statusText || `HTTP error ${response.status}`);
        throw new Error(msg);
      }

      // Expecting { status: 'processing', operationId, statusUrl }
      return { success: true, error: null, operationId: result.operationId, statusUrl: result.statusUrl };
    } catch (err: any) {
      console.error("Error triggering Instagram index:", err);
      return { success: false, error: err.message || ERROR_MESSAGES.SOCIAL.INDEXING_FAILED };
    }
  }

  /**
   * Poll index_operations table for a given operationId until it completes or times out.
   * Success statuses treated as: 'success', 'succeeded', 'completed', 'done'
   * Failure statuses treated as: 'failed', 'error', 'canceled', 'cancelled'
   */
  static async waitForIndexSuccess(
    operationId: string,
    options?: { intervalMs?: number; timeoutMs?: number }
  ): Promise<{ status: string }> {
    const intervalMs = options?.intervalMs ?? 30000;
    const timeoutMs = options?.timeoutMs ?? 5 * 60 * 1000; // 5 minutes default

    const start = Date.now();
    const successSet = new Set(['success', 'partial_success', 'succeeded', 'completed', 'done']);
    const failureSet = new Set(['failed', 'error', 'canceled', 'cancelled']);

    while (true) {
      if (Date.now() - start > timeoutMs) {
        throw new Error(ERROR_MESSAGES.SOCIAL.INDEXING_TIMEOUT);
      }

      try {
        // Poll our server route which proxies APIM status and upserts into Supabase
        const res = await fetch(`/api/instagram/index-status?operationId=${encodeURIComponent(operationId)}`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          cache: 'no-store'
        } as RequestInit);
        const json = await res.json();
        if (!res.ok) {
          console.warn('waitForIndexSuccess: status endpoint returned non-OK', json?.error || res.statusText);
        }

        const status = (json?.status || '').toString().toLowerCase();
        if (status) {
          if (successSet.has(status)) {
            return { status };
          }
          if (failureSet.has(status)) {
            throw new Error(`${ERROR_MESSAGES.SOCIAL.INDEXING_FAILED} with status: ${status}`);
          }
        }
      } catch (e) {
        // Swallow transient errors and continue until timeout
        console.debug('waitForIndexSuccess: transient error', e);
      }

      await new Promise(res => setTimeout(res, intervalMs));
    }
  }

  static setSessionDisconnected(): void {
    sessionStorage.setItem('instagram_session_disconnected', 'true');
    localStorage.setItem('instagram_auth_status', 'false');
  }

  static isSessionDisconnected(): boolean {
    return sessionStorage.getItem('instagram_session_disconnected') === 'true';
  }

  static clearSessionDisconnected(): void {
    sessionStorage.removeItem('instagram_session_disconnected');
  }
} 